import React, { ChangeEvent } from 'react'
import { FormControlLabel, Checkbox, Tooltip } from '@material-ui/core'
import { FormObjectType, ISelectInput } from '../../types/pack'
import { IGroup } from '../../types/group'
import { IQuery, FilterOptions, SortOptions } from '../../types/search'
import {
  parseNestedQuery,
  transformIntoTag,
  dateCaptions,
} from '../../services/search'
import {
  getTagsSuggestions,
  debouncedGetTopicsSuggestions,
  getSluglineSuggestions,
  topicsTypeaheadToFormAdapter,
} from '../../services/typeahead'
import { StyledSearchFilters } from '../MultiToggle/styled'
import { DateFilters } from './DateFilters'
import { TypeaheadSluglineOrigin } from '../TypeaheadSluglineOrigin'
import { Caption } from '../StyledText'
import { MultiToggle, MultiToggleButton } from '../MultiToggle'
import { TypeaheadSelect } from '../TypeaheadSelect'
import StyledSelect from '../StyledSelect'

export interface ISearchFilters {
  groupId: string
  setFilters: (filters: IQuery) => void
  isConsumer: boolean
  theme: any
  groups: IGroup[]
  location: {
    search?: string
  }
}

export const SearchFilters = ({
  groupId,
  setFilters,
  isConsumer,
  theme,
  groups,
  location: { search = '' },
}: ISearchFilters) => {
  const filters: IQuery = parseNestedQuery(search)
  const handleChange = (value: any, fieldName: string) => {
    setFilters({
      ...filters,
      [fieldName]: value,
    })
  }

  const sluglineValidationFailed =
    !!filters.sluglines && filters.sluglines.length > 64

  const transformedTopics = filters.topicCodes
    ? filters.topicCodes.map(topicsTypeaheadToFormAdapter)
    : []

  return (
    <StyledSearchFilters>
      <Caption>Filters</Caption>
      {isConsumer && (
        <StyledSelect
          label="Group"
          placeholder="Group"
          id="group-type"
          value={filters.group || ''}
          onChange={(e) => handleChange(e.target.value, 'group')}
        >
          {[
            { label: 'All groups', value: 'all' },
            ...groups.map((el: IGroup) => ({
              ...el,
              value: el.name,
              label: el.shortTitle,
            })),
          ]}
        </StyledSelect>
      )}
      {!isConsumer && (
        <>
          <MultiToggle
            activeValue={filters.currentUser ? 'my' : 'all'}
            handleChange={(value: string): void =>
              setFilters({ ...filters, currentUser: value === 'my' })
            }
            theme={theme}
          >
            <MultiToggleButton value="my">My graphics</MultiToggleButton>
            <MultiToggleButton value="all">All graphics</MultiToggleButton>
          </MultiToggle>
          <StyledSelect
            id="graphic-date"
            value={filters.date || FilterOptions.modified}
            onChange={({
              target: { value },
            }: ChangeEvent<HTMLInputElement>): void =>
              setFilters({
                ...filters,
                date: FilterOptions[value],
                orderBy: SortOptions[value],
              })
            }
          >
            {[
              {
                label: dateCaptions.modified,
                value: FilterOptions.modified,
              },
              {
                label: dateCaptions.nextScheduleDate,
                value: FilterOptions.nextScheduleDate,
              },
              {
                label: dateCaptions.expiryDate,
                value: FilterOptions.expiryDate,
              },
            ]}
          </StyledSelect>
        </>
      )}

      {isConsumer && <Caption variant="h4">Published</Caption>}

      <DateFilters
        filters={filters}
        setFilters={setFilters}
        editorPortal={!isConsumer}
      />

      {isConsumer && (
        <StyledSelect
          label="Type"
          id="graphic-type"
          value={filters.graphicType || ''}
          placeholder="Type"
          onChange={(e) => handleChange(e.target.value, 'graphicType')}
        >
          {[
            { value: '', label: 'Any' },
            { value: 'datastream_chart', label: 'DatastreamChart' },
            { value: 'interactive', label: 'Interactive' },
            { value: 'image', label: 'Image' },
            { value: 'document', label: 'Document' },
            { value: 'eps', label: 'EPS' },
          ]}
        </StyledSelect>
      )}
      {!isConsumer && (
        <StyledSelect
          name="status"
          label="Status"
          id="graphic-status"
          placeholder="Status"
          value={filters.status || 'any'}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          {[
            { label: 'Any', value: 'any' },
            { label: 'Published', value: 'published' },
            { label: 'Unpublished', value: 'unpublished' },
          ]}
        </StyledSelect>
      )}
      {!isConsumer && (
        <TypeaheadSelect
          isMulti
          name="tags"
          label="Tags"
          placeholder="Filter by tags"
          loadOptions={getTagsSuggestions(groupId)}
          changeListener={(tags: ISelectInput[]) => {
            setFilters({
              ...filters,
              tags: tags ? tags.map((tag) => tag.value) : [],
            })
          }}
          value={transformIntoTag(filters.tags)}
        />
      )}
      <TypeaheadSelect
        isMulti
        name="topicCodes"
        label="Topics"
        placeholder="Filter by topics"
        loadOptions={debouncedGetTopicsSuggestions}
        changeListener={(topics: FormObjectType[]) => {
          setFilters({
            ...filters,
            topicCodes: topics
              ? topics.map(({ rcsId, value, shortName, n2000 }) => ({
                  rcsId,
                  label: shortName,
                  value,
                  shortName,
                  n2000,
                }))
              : [],
          })
        }}
        value={transformedTopics}
      />
      <TypeaheadSluglineOrigin
        name="sluglines"
        label="Slug"
        placeholder="Filter by slug"
        helperText={
          sluglineValidationFailed
            ? 'The text is too long, maximum number of characters is 64.'
            : ''
        }
        error={sluglineValidationFailed}
        loadOptions={getSluglineSuggestions}
        onChange={(e) => {
          const { value, selectionStart } = e.target
          e.target.value = value.toUpperCase()
          e.target.selectionStart = selectionStart
          e.target.selectionEnd = selectionStart
          setFilters({
            ...filters,
            sluglines: e.target.value,
          })
        }}
        onSelect={(sluglines) => handleChange(sluglines, 'sluglines')}
        value={filters.sluglines || ''}
      />
      {isConsumer && (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.includeMedia}
              color="primary"
              onChange={(e) => handleChange(e.target.checked, 'includeMedia')}
            />
          }
          label="Include media"
          id="include-media"
        />
      )}
      <Tooltip
        title='Enables logical operators usage such as - + & | ! ( ) { } [ ] ^ " ~ * ? :'
        aria-label="advanced search label"
      >
        <FormControlLabel
          id="advanced-search"
          label="Advanced search"
          control={
            <Checkbox
              checked={!!filters.advancedQuery}
              color="primary"
              onChange={(e) => handleChange(e.target.checked, 'advancedQuery')}
            />
          }
        />
      </Tooltip>
    </StyledSearchFilters>
  )
}
